import {
  faCircleInfo,
  faEllipsisVertical,
  faFile,
  faUserCheck,
  faUserClock,
  faUserXmark,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Divider, Grid, Tooltip, Typography, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BellIcon } from "src/assets/icons";
import { isAuthorizedEeGroup } from "src/custom-hooks/useAuthorization/useAuthorization";
import { AUTHORIZATIONS } from "src/utils/constants/authorization-constants";
import DeleteExpenseReport from "./DeleteExpenseReport/DeleteExpenseReport";
import ExpenseReportStatus from "./ExpenseReportStatus/ExpenseReportStatus";
import { Props } from "./interfaces";
import {
  AcceptedDifferenceamountContainer,
  AdditionalText,
  Amount,
  AmountContainer,
  CommentText,
  CountryIconsContainer,
  DataContainer,
  DateAndCommentContainer,
  DateContainer,
  DateDescriptionBlock,
  DescriptionCountriesContainer,
  DifferenceAmount,
  EmployeeDataContainer,
  ExpenseReportContainer,
  ExpenseReportDetailsContainer,
  ExpenseReportWrapper,
  IconsContainer,
  IconTextContainer,
  MainInfoContainer,
  NotesInfoContainer,
  StatusContainer,
  StatusesContainer,
} from "./styles";
import { uniqueCountries } from "./utils";
import FlagIcon from "../../../../../containers/FlagIcon/FlagIcon";
import { useAppSelector } from "../../../../../custom-hooks/redux/redux";
import SimplePopover from "../../../../../reusable-components/SimplePopover/SimplePopover";
import { EXPENSE_REPORT_STATUS } from "../../../../../utils/constants/constants";
import { ROW_CLASSNAME } from "../../../../../utils/export-expense-reports-utils/export-expense-reports-as-pdf";
import { computeMonthName } from "../../../../../utils/time-utils";
import { ExpenseReportView } from "../../utils";

const ExpenseReportItem = ({
  expenseReport,
  view,
  handleNotifyParent,
  readOnly,
  hideAuditNotes = false,
  preloadedFlags,
}: Props) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const tRP = "components.expense_reports";
  const { currentUserData, currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const isAuditor = useMemo(() => {
    return isAuthorizedEeGroup(
      [AUTHORIZATIONS.EmployeesAccess.AuditTravelAndExpenses],
      currentUserAuthorizations,
      expenseReport.employee?.groups?.map((group) => group?.id) ?? [],
      true
    );
  }, [currentUserAuthorizations, expenseReport.employee?.groups]);

  const hasAuditNotesNotification = useMemo(() => {
    return !!expenseReport?.travelExpenses?.find(
      (travelExpense) =>
        !!travelExpense?.auditNotes?.length &&
        travelExpense.auditNotes[0]?.createdByUser?.id !== currentUserData?.user?.id &&
        !travelExpense.auditNotes[0].travelExpenseAuditNoteReviews.find(
          (preview) => preview.createdByUser?.id === currentUserData?.user?.id
        )
    );
  }, [expenseReport?.travelExpenses, currentUserData?.user?.id]);

  const questionRaisedByAuditor = useMemo(() => {
    return (
      isAuditor &&
      view !== ExpenseReportView.employee &&
      !!expenseReport.travelExpenses.find(
        (travelExpense) =>
          !!travelExpense.auditNotes.length &&
          travelExpense.auditNotes[0]?.createdByUser?.id === currentUserData?.user?.id
      )
    );
  }, [currentUserData, expenseReport.travelExpenses, isAuditor, view]);

  const hasBusinessPurposeDocument = useMemo(() => {
    return !!expenseReport.preApprovalDocument;
  }, [expenseReport.preApprovalDocument]);

  const allCountries = expenseReport.travelExpenses?.map((travelExpense) => travelExpense.country) ?? [];

  const countries = uniqueCountries(allCountries);

  const acceptedAmount = expenseReport.travelExpenses
    .filter((travelExpense) => travelExpense.auditAccepted !== false)
    ?.map((travelExpense) => travelExpense.acceptedAmount)
    .reduce((prev, curr) => prev + curr, 0);
  const differenceAmount = expenseReport.travelExpenses
    .filter((travelExpense) => travelExpense.auditAccepted !== false)
    ?.map((travelExpense) => travelExpense.difference)
    .reduce((prev, curr) => prev + curr, 0);

  return (
    <ExpenseReportWrapper className={ROW_CLASSNAME}>
      <ExpenseReportContainer>
        <DataContainer container>
          <EmployeeDataContainer
            highlight={hasAuditNotesNotification || questionRaisedByAuditor}
            flex={1}
          >
            <Typography>{expenseReport.employee?.name}</Typography>
            <AdditionalText title={expenseReport.legalEntity?.name}>{expenseReport.legalEntity?.code}</AdditionalText>
          </EmployeeDataContainer>

          <Divider
            orientation="vertical"
            flexItem
            style={{
              margin: "10px 0px",
            }}
          />

          <MainInfoContainer
            flex={6}
            highlight={hasAuditNotesNotification || questionRaisedByAuditor}
          >
            {(hasAuditNotesNotification || questionRaisedByAuditor) && !hideAuditNotes && (
              <Grid style={{ display: "flex" }}>
                <Grid flex={2}></Grid>
                <NotesInfoContainer flex={3}>
                  <IconTextContainer>
                    <BellIcon
                      width="15px"
                      height="15px"
                      fill={theme.palette.error.main}
                    />

                    <Typography sx={{ color: theme.palette.error.main }}>
                      {!questionRaisedByAuditor
                        ? view === ExpenseReportView.employee || (view === ExpenseReportView.manager && !isAuditor)
                          ? `${t(`${tRP}.question_from_auditor`)}`
                          : `${t(`${tRP}.pending_response`)}`
                        : `${t(`${tRP}.raised_a_question`)}`}
                    </Typography>
                  </IconTextContainer>
                </NotesInfoContainer>
                <Grid flex={0.8}></Grid>
              </Grid>
            )}
            <ExpenseReportDetailsContainer container>
              <DateAndCommentContainer flex={2}>
                <DateDescriptionBlock>
                  <Grid style={{ display: "flex", gap: "5px", alignContent: "center" }}>
                    <CommentText title={expenseReport.businessPurposeDescription}>
                      <span>{expenseReport.businessPurposeDescription}</span>
                    </CommentText>
                    <IconsContainer>
                      {hasBusinessPurposeDocument && (
                        <FontAwesomeIcon
                          title={`${t(`${tRP}.includes_businesss_puropose_doc`)}`}
                          icon={faFile}
                          color={theme.palette.secondary.light}
                          style={{ width: "20px", height: "24px" }}
                        />
                      )}
                    </IconsContainer>
                  </Grid>

                  <DateContainer>
                    <Typography style={{ fontSize: "35px" }}>
                      {new Date(expenseReport.date).getDate() > 9
                        ? new Date(expenseReport.date).getDate()
                        : `0${new Date(expenseReport.date).getDate()}`}
                    </Typography>
                    <Grid>
                      <Typography>{computeMonthName(new Date(expenseReport.date).getMonth())}</Typography>
                      <Typography>{new Date(expenseReport.date).getFullYear()}</Typography>
                    </Grid>
                  </DateContainer>
                </DateDescriptionBlock>
                {/* <AdditionalText>{intlFormatDateShort(new Date(expenseReport.date))}</AdditionalText> */}
              </DateAndCommentContainer>

              <AmountContainer flex={3}>
                <>
                  {(expenseReport.status?.id === EXPENSE_REPORT_STATUS.approved.id ||
                    expenseReport.status?.id === EXPENSE_REPORT_STATUS.paid.id) &&
                  acceptedAmount !== expenseReport.totalAmount ? (
                    <AcceptedDifferenceamountContainer>
                      <Amount
                        title={`${t(`${tRP}.amount`)}: ${acceptedAmount.toLocaleCustom()} ${
                          expenseReport.currency?.code
                        }`}
                      >
                        {`${acceptedAmount.toLocaleCustom()} ${expenseReport.currency?.code}`}
                      </Amount>
                      <DifferenceAmount style={{ marginRight: "-30px" }}>
                        <Typography>{`${differenceAmount.toLocaleCustom()} ${
                          expenseReport.currency?.code
                        }`}</Typography>
                        <SimplePopover
                          buttonType="icon"
                          buttonContent={
                            <FontAwesomeIcon
                              icon={faCircleInfo}
                              color={theme.palette.secondary.light}
                            />
                          }
                        >
                          <Grid style={{ padding: "20px" }}>
                            {expenseReport.auditorComment ?? (
                              <Typography
                                variant="body2"
                                style={{ color: theme.palette.secondary.light }}
                              >
                                <i>{`${t(`${tRP}.no_explanation`)}`}</i>
                              </Typography>
                            )}
                          </Grid>
                        </SimplePopover>
                      </DifferenceAmount>
                    </AcceptedDifferenceamountContainer>
                  ) : (
                    <Amount
                      title={`${t(`${tRP}.amount`)}: ${expenseReport.totalAmount.toLocaleCustom()}${
                        expenseReport.currency?.code
                      }`}
                    >
                      {`${expenseReport.totalAmount.toLocaleCustom()} ${expenseReport.currency?.code}`}
                    </Amount>
                  )}
                  {/* {`${expenseReport.totalAmount.toLocaleCustom()} ${expenseReport.currency?.code}`} */}
                </>
              </AmountContainer>

              <DescriptionCountriesContainer flex={0.8}>
                {!!expenseReport.travelExpenses.length && !!countries.length && (
                  <>
                    {countries.length > 3 && (
                      <SimplePopover
                        buttonType="icon"
                        buttonContent={<FontAwesomeIcon icon={faEllipsisVertical} />}
                        flow="column"
                        anchorOrigin={{
                          vertical: "bottom",
                          horizontal: "center",
                        }}
                        transformOrigin={{
                          vertical: "top",
                          horizontal: "center",
                        }}
                      >
                        <CountryIconsContainer>
                          {countries.slice(3).map((country) => (
                            <Grid
                              key={country.id}
                              title={country?.name ?? ""}
                            >
                              {country?.code && (
                                <FlagIcon
                                  countryCode={country.code}
                                  customStyles={{ borderRadius: "10px", width: "35px", height: "35px" }}
                                  predefinedSRC={preloadedFlags?.[country.code]}
                                />
                              )}
                            </Grid>
                          ))}
                        </CountryIconsContainer>
                      </SimplePopover>
                    )}
                    {countries.slice(0, 3).map((country) => (
                      <Grid
                        key={country.id}
                        title={country?.name ?? ""}
                        sx={{ height: "35px" }}
                      >
                        {country?.code && (
                          <FlagIcon
                            countryCode={country.code}
                            customStyles={{ borderRadius: "10px", width: "35px", height: "35px" }}
                            predefinedSRC={preloadedFlags?.[country.code]}
                          />
                        )}
                      </Grid>
                    ))}
                  </>
                )}
              </DescriptionCountriesContainer>
            </ExpenseReportDetailsContainer>
          </MainInfoContainer>

          <StatusesContainer
            flex={1}
            status={expenseReport.status?.id ?? ""}
          >
            <StatusContainer>
              {expenseReport.needAcceptance && (
                <IconsContainer>
                  {expenseReport.needAcceptance &&
                    (expenseReport.acceptedByClient ? (
                      <FontAwesomeIcon
                        icon={faUserCheck}
                        color={"greenyellow"}
                        style={{ width: "19px", height: "17px" }}
                      />
                    ) : expenseReport.rejectedByClient ? (
                      <FontAwesomeIcon
                        icon={faUserXmark}
                        color={theme.palette.error.main}
                        style={{ width: "19px", height: "17px" }}
                      />
                    ) : (
                      <FontAwesomeIcon
                        icon={faUserClock}
                        color={"gray"}
                        style={{ width: "19px", height: "17px" }}
                      />
                    ))}
                </IconsContainer>
              )}

              <Grid>
                <ExpenseReportStatus status={expenseReport.status} />
              </Grid>
            </StatusContainer>
            <Grid style={{ marginTop: "5px", textAlign: "center" }}>
              <Tooltip
                title={
                  expenseReport.travelExpenses.length > 0
                    ? `${
                        expenseReport.travelExpenses.filter((travelExpense) => travelExpense.auditAccepted !== null)
                          .length
                      } ${t(`${tRP}.audited_out_of`)} ${expenseReport.travelExpenses.length}`
                    : `${t(`${tRP}.no_receipts`)}`
                }
              >
                <Typography
                  variant="body2"
                  style={{ color: theme.palette.secondary.light }}
                >
                  {expenseReport.travelExpenses.length > 0
                    ? `${(
                        (expenseReport.travelExpenses.filter((travelExpense) => travelExpense.auditAccepted !== null)
                          .length /
                          expenseReport.travelExpenses.length) *
                        100
                      ).toFixed(0)}% ${t(`${tRP}.audited`)} `
                    : `${t(`${tRP}.no_receipts`)}`}
                </Typography>
              </Tooltip>
            </Grid>
          </StatusesContainer>
        </DataContainer>
      </ExpenseReportContainer>

      {view === ExpenseReportView.employee &&
        (expenseReport.status?.id === EXPENSE_REPORT_STATUS.draft.id ||
          expenseReport.status?.id === EXPENSE_REPORT_STATUS.returned_to_employee.id) &&
        !readOnly && (
          <DeleteExpenseReport
            expenseReport={expenseReport}
            handleNotifyParent={handleNotifyParent}
          />
        )}
    </ExpenseReportWrapper>
  );
};

export default ExpenseReportItem;
